// The incremental half of the tiered SQLite snapshot load (SLICES.md § S7
// items 1 + 2, engine parity from item 8). `sqliteSchemaCache.ts` owns the
// cache entry, the TTL and the phase orchestration; this module owns the DATA
// STRUCTURE those phases fill in.
//
// Mirrors `mssqlSnapshotMerge.ts` on top of `liveSnapshotMerge.ts`. Two SQLite
// specifics:
//
//   * a relation is keyed by `<attach-alias>|<name>`, not by name alone — the
//     same table name can exist in `main` and in an ATTACHed database, and
//     only the qualified form tells them apart. The bare-name lookup indexes
//     keep their own-database-wins rule.
//   * phase 2 arrives as BATCHES of fully hydrated relations (columns +
//     indexes + FKs together), because every PRAGMA is per table. One batch is
//     one merge + one republish.

import type { FkEdge, SchemaSnapshot, TableMeta } from '@/core/engines/intellisenseTypes';
import {
  projectSqliteForeignKeys,
  projectSqliteTableEntry,
} from '@/features/intellisense/schemaBus/adapters/sqliteAdapter';

import { forEachChunkYielding } from './introspectionPaging';
import { applyPerRelationPage, createLiveSnapshot, type LiveSnapshot } from './liveSnapshotMerge';
import type {
  SqliteHydratedRelation,
  SqliteRelationEntry,
} from './sqliteSchemaIntrospection';
import type {
  SqliteSchemaColumn,
  SqliteSchemaObject,
  SqliteSchemaSnapshot,
} from './sqliteSchemaTypes';

export type SqliteLiveSnapshot = LiveSnapshot<SqliteSchemaSnapshot, SqliteSchemaObject> & {
  /** Mutable array backing `projected.foreignKeys`. */
  readonly projectedForeignKeys: FkEdge[];
  /** Edges contributed by each relation index, so a re-merge can replace
   *  exactly that relation's edges instead of rebuilding the whole list. */
  readonly edgesByEntry: Map<number, FkEdge[]>;
};

/** `<attach-alias>|<name>` — `main` objects carry an empty alias. */
export function sqliteTableKey(name: string, schema?: string): string {
  return `${(schema ?? '').toLowerCase()}|${name.toLowerCase()}`;
}

/**
 * Carry the previous refresh's columns / indexes / FK rows onto the freshly
 * fetched relation list — stale-while-revalidate, so a forced refresh does not
 * blank every relation's columns for as long as the PRAGMA walk takes.
 */
export function carryOverSqliteIntrospection(
  previous: readonly SqliteSchemaObject[] | undefined,
  fresh: readonly SqliteRelationEntry[],
): SqliteSchemaObject[] {
  const byKey = new Map<string, SqliteSchemaObject>();
  for (const entry of previous ?? []) byKey.set(sqliteTableKey(entry.name, entry.schema), entry);
  return fresh.map((entry) => {
    const prior = byKey.get(sqliteTableKey(entry.name, entry.schema));
    return {
      name: entry.name,
      type: entry.type,
      sql: entry.sql,
      ...(entry.schema ? { schema: entry.schema } : {}),
      columns: prior ? prior.columns.slice() : [],
      indexes: prior ? prior.indexes.slice() : [],
      foreignKeys: prior ? prior.foreignKeys.slice() : [],
    };
  });
}

/**
 * Register one relation in the bare-name lookup indexes. Own-database objects
 * win the unqualified key: SQLite resolves an unqualified name against `main`
 * before the ATTACHed databases, and hover / the column index key on the bare
 * name.
 */
function indexRelation(live: SqliteLiveSnapshot, obj: SqliteSchemaObject): void {
  const native = live.native;
  const key = obj.name.toLowerCase();
  if (obj.schema && native.objectsByNameLower[key]) return;
  native.objectsByNameLower[key] = obj;
  native.columnsByObjectNameLower[key] = obj.columns;
  for (const column of obj.columns) {
    (native.columnIndexByNameLower[column.name.toLowerCase()] ??= []).push({
      objectName: obj.name,
      objectType: obj.type,
      column,
    });
  }
}

/** Drop one relation's contributions to the column-name index (O(bucket)). */
function unindexColumns(
  live: SqliteLiveSnapshot,
  obj: SqliteSchemaObject,
  columns: readonly SqliteSchemaColumn[],
): void {
  const index = live.native.columnIndexByNameLower;
  for (const column of columns) {
    const bucketKey = column.name.toLowerCase();
    const bucket = index[bucketKey];
    if (!bucket) continue;
    const kept = bucket.filter((row) => row.objectName !== obj.name);
    if (kept.length === 0) delete index[bucketKey];
    else index[bucketKey] = kept;
  }
}

/**
 * Build the live snapshot for a freshly fetched relation list, projecting the
 * relations in event-loop-yielding chunks so even a 200k-relation database
 * never occupies the main thread for a single long task.
 */
export async function createSqliteLiveSnapshot(
  native: SqliteSchemaSnapshot,
  options?: { readonly yieldBetweenChunks?: () => Promise<void> },
): Promise<SqliteLiveSnapshot> {
  const entries = native.tables.concat(native.views);
  const projectedForeignKeys: FkEdge[] = [];

  const attachedDatabases = (native.attachedDatabases ?? []).map((db) => ({
    alias: db.name,
    path: db.file,
  }));

  const base = await createLiveSnapshot<SqliteSchemaSnapshot, SqliteSchemaObject>({
    native,
    entries,
    keyOf: (entry) => sqliteTableKey(entry.name, entry.schema),
    projectEntry: (entry) => projectSqliteTableEntry(entry, native.database),
    buildProjected: (tables: TableMeta[]): SchemaSnapshot => ({
      engineId: 'sqlite',
      connectionId: native.connectionId,
      database: native.database,
      schemas: [],
      tables,
      // SQLite has no user-defined routines surfaced through this cache
      // (signature help uses a static built-in table).
      routines: [],
      foreignKeys: projectedForeignKeys,
      serverVersion: native.serverVersion || undefined,
      attachedDatabases,
      builtAt: native.builtAt,
    }),
    yieldBetweenChunks: options?.yieldBetweenChunks,
  });

  // The native `tables` / `views` arrays must hold the SAME entry objects the
  // live snapshot indexes, so a consumer reading the native cache sees phase
  // 2's merges.
  native.tables = base.entries.filter((e) => e.type === 'table');
  native.views = base.entries.filter((e) => e.type === 'view');

  const live: SqliteLiveSnapshot = {
    ...base,
    projectedForeignKeys,
    edgesByEntry: new Map<number, FkEdge[]>(),
  };

  // Seed the bare-name lookups + the carried-over edges over whatever the
  // carry-over brought along (nothing at all on a cold load), in yielding
  // chunks. Phase 2 keeps them in step incrementally from there.
  native.objectsByNameLower = {};
  native.columnsByObjectNameLower = {};
  native.columnIndexByNameLower = {};
  await forEachChunkYielding(
    live.entries,
    (chunk) => {
      for (const obj of chunk) indexRelation(live, obj);
    },
    { yieldBetweenChunks: options?.yieldBetweenChunks },
  );
  for (let i = 0; i < live.entries.length; i += 1) {
    const entry = live.entries[i]!;
    if (entry.type !== 'table' || entry.foreignKeys.length === 0) continue;
    const edges = projectSqliteForeignKeys(entry);
    live.edgesByEntry.set(i, edges);
    projectedForeignKeys.push(...edges);
  }

  return live;
}

/**
 * Merge one BATCH of hydrated relations — columns, indexes and FK rows for
 * each one, all at once, because a SQLite PRAGMA answers per table. Only the
 * relations in the batch are re-projected, and the bare-name lookups plus the
 * flat FK edge list are updated per relation rather than rebuilt.
 */
export function applySqliteRelationBatch(
  live: SqliteLiveSnapshot,
  batch: readonly SqliteHydratedRelation[],
): void {
  const touched: number[] = [];
  applyPerRelationPage(live, 'detail', batch, {
    keyOf: (item) => sqliteTableKey(item.name, item.schema),
    append: (entry, list) => {
      // A PRAGMA answers for the WHOLE relation, so a batch always replaces
      // rather than appends — but never mutates the existing arrays, which a
      // caller holding the previous snapshot may still be reading.
      const hydrated = list[list.length - 1]!;
      unindexColumns(live, entry, entry.columns);
      entry.columns = hydrated.columns;
      entry.indexes = hydrated.indexes;
      entry.foreignKeys = hydrated.foreignKeys;
      const key = entry.name.toLowerCase();
      if (!entry.schema || live.native.objectsByNameLower[key] === entry) {
        live.native.objectsByNameLower[key] = entry;
        live.native.columnsByObjectNameLower[key] = entry.columns;
      }
      for (const column of entry.columns) {
        (live.native.columnIndexByNameLower[column.name.toLowerCase()] ??= []).push({
          objectName: entry.name,
          objectType: entry.type,
          column,
        });
      }
      const index = live.tableIndex.get(sqliteTableKey(entry.name, entry.schema));
      if (index !== undefined) touched.push(index);
    },
  });

  if (touched.length === 0) return;
  for (const index of touched) {
    const entry = live.entries[index];
    if (!entry) continue;
    live.edgesByEntry.set(
      index,
      entry.type === 'table' ? projectSqliteForeignKeys(entry) : [],
    );
  }
  // The flat edge list is small relative to the catalog (one entry per FK, not
  // per key column), so refilling it from the per-relation map keeps the merge
  // simple without the append-twice hazard a straddling relation would create.
  live.projectedForeignKeys.length = 0;
  for (const edges of live.edgesByEntry.values()) live.projectedForeignKeys.push(...edges);
}
